import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import LeadModal from "@/components/marketing/LeadModal";
import {
  ArrowRight, Building2, ShoppingBag, GraduationCap, School, Truck,
  Smartphone, Monitor, Tablet, ScanLine, Globe2, ChefHat, BarChart3,
  Wallet, Layers, Cpu, ShieldCheck, Zap, Cloud, Code2, Network, Sparkles,
  TrendingUp, CheckCircle2,
} from "lucide-react";

const STATS = [
  { value: "50+",      label: "Food court locations" },
  { value: "200+",     label: "Vendor menus" },
  { value: "27,000+",  label: "Active SKUs" },
  { value: "3.9M+",    label: "Orders processed in 2025" },
  { value: "99.9%",    label: "Uptime SLA" },
];

const VERTICALS = [
  {
    to: "/corporate", icon: Building2, accent: "bg-blue-50 text-blue-700 border-blue-200",
    title: "Corporate Campuses",
    blurb: "Subsidised meal programs, employee wallets, cashless cafeteria operations, and HRMS-grade reporting for tech parks and BPOs.",
    chips: ["Meal subsidy engine", "SSO with HRMS", "Multi-vendor billing"],
  },
  {
    to: "/malls", icon: ShoppingBag, accent: "bg-rose-50 text-rose-700 border-rose-200",
    title: "Malls & Food Courts",
    blurb: "Unified order capture across kiosk, app, web and POS — with cart-level splits, KOT-per-vendor, and weekly settlement.",
    chips: ["Self-serve kiosks", "Multi-vendor cart", "Auto settlement"],
  },
  {
    to: "/education", icon: GraduationCap, accent: "bg-violet-50 text-violet-700 border-violet-200",
    title: "Colleges & Universities",
    blurb: "Hostel mess + day cafeteria + canteen — one wallet, one ID card, one ledger. Built for 20,000-student campuses.",
    chips: ["Mess subscriptions", "Hostel + canteen", "Parent visibility"],
  },
  {
    to: "/schools", icon: School, accent: "bg-amber-50 text-amber-700 border-amber-200",
    title: "K-12 Schools",
    blurb: "RFID tap-to-pay, allergen-aware parental controls, meal plans, pre-orders, and CSR / festival specials.",
    chips: ["RFID terminals", "Parental controls", "Day-end reports"],
  },
  {
    to: "/expressways", icon: Truck, accent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    title: "Expressway Food Courts",
    blurb: "High-throughput, offline-resilient POS for transit-led food courts with no internet to spare and zero patience for queues.",
    chips: ["Offline POS", "60-sec ticket", "Vendor payouts"],
  },
];

const CAPABILITIES = [
  { icon: ScanLine,    title: "Order capture, anywhere",
    desc: "Kiosk, mobile app, web QR, waiter app, single-vendor POS, multi-vendor POS — all writing to one order ledger." },
  { icon: ChefHat,     title: "Kitchen orchestration",
    desc: "KOT splitting per vendor, KDS sync, table maps, delivery routing, and SLA timers built into every flow." },
  { icon: Wallet,      title: "Wallets & subsidies",
    desc: "Corporate wallets, parent wallets, staff points, employer-funded meal subsidies, auto-recharge — all rule-driven." },
  { icon: BarChart3,   title: "Settlements & analytics",
    desc: "Weekly vendor payouts, SKU-level P&L, AI demand forecasting, scheduled reports — straight to inbox or BI tools." },
  { icon: ShieldCheck, title: "Compliance & control",
    desc: "GST invoicing, audit trail per transaction, role-based access, FSSAI-ready expense ledgers, and tenant isolation." },
  { icon: Network,     title: "Integrations that don't fight you",
    desc: "Petpooja, UrbanPiper, Food Engine, Rishta POS, Razorpay, Paytm, PhonePe, Cashfree, EdKonnect SSO, Karix, SendGrid." },
];

const CHANNELS = [
  { icon: Smartphone, label: "Mobile app",       hint: "iOS & Android" },
  { icon: Globe2,     label: "Web / QR",         hint: "No install" },
  { icon: Tablet,     label: "Self-serve kiosk", hint: "Touchscreen" },
  { icon: Monitor,    label: "Multi-vendor POS", hint: "Counter terminal" },
  { icon: ChefHat,    label: "Waiter & KDS",     hint: "Tablet apps" },
  { icon: ScanLine,   label: "RFID tap",         hint: "Sub-1.5s" },
];

const TECH_PILLARS = [
  { icon: Cpu,         title: "100% in-house",      desc: "Every line of code, every workflow — built and owned by us. Zero vendor lock-in on critical paths." },
  { icon: Smartphone,  title: "Mobile-first",        desc: "Every interface designed thumb-first. The web admin is a desktop bonus, not the centerpiece." },
  { icon: Sparkles,    title: "AI-assisted",         desc: "Demand forecasting, menu pricing nudges, anomaly detection — quietly running behind every dashboard." },
  { icon: Cloud,       title: "Cloud-native",        desc: "Horizontally scalable, 99.9% uptime SLA, regional fail-over, blue/green deploys." },
  { icon: Layers,      title: "One data model",      desc: "One identity per person, one wallet, one consumption ledger — across vendors, channels, and locations." },
  { icon: Code2,       title: "API-first",           desc: "Every screen is a thin layer on a documented REST API. Build your own front-end, your own kiosk, your own reports." },
];

const ARCH_NODES_CUSTOMER = [
  { label: "Mobile App",       icon: Smartphone },
  { label: "Web / QR",         icon: Globe2 },
  { label: "Self-Serve Kiosk", icon: Tablet },
];
const ARCH_NODES_OPS = [
  { label: "Multi-Vendor POS", icon: Monitor },
  { label: "Single-Vendor POS",icon: Monitor },
  { label: "Waiter App",       icon: ChefHat },
];
const ARCH_NODES_ADMIN = [
  { label: "Admin ERP",        icon: Layers },
  { label: "Vendor App",       icon: Wallet },
  { label: "Analytics & AI",   icon: BarChart3 },
];

export default function ProductLanding() {
  const [leadOpen, setLeadOpen] = useState(false);
  const openLead = () => setLeadOpen(true);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // wait one frame for sections to mount, then scroll
    const t = setTimeout(tryScroll, 100);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen bg-cream-50 text-ink">
      <MarketingNav onCta={openLead}/>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-20 w-[480px] h-[480px] rounded-full bg-brand/10 blur-3xl"/>
          <div className="absolute -bottom-32 -left-20 w-[480px] h-[480px] rounded-full bg-sun/30 blur-3xl"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink/5 border border-ink/10 text-[11px] font-bold uppercase tracking-[0.18em] text-ink-700">
            <Zap size={11} className="text-brand"/> One platform · five verticals
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mt-5 max-w-4xl leading-[1.05]">
            The operating system for <span className="text-brand">multi-vendor dining</span>.
          </h1>
          <p className="text-base sm:text-lg text-ink-600 mt-5 max-w-2xl leading-relaxed">
            Grubpe powers ordering, payments, settlements, and analytics for corporate campuses, malls, universities, schools, and expressway food courts —
            on one in-house stack that we build, deploy, and own end-to-end.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button onClick={openLead} data-testid="hero-cta-demo"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ink text-white font-semibold hover:bg-ink-700 active:scale-95">
              Request a demo <ArrowRight size={16}/>
            </button>
            <a href="#stack" data-testid="hero-cta-stack"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-border text-ink-700 font-semibold hover:border-ink">
              Explore the tech <ArrowRight size={16}/>
            </a>
          </div>

          {/* Channel chips */}
          <div className="mt-12 grid grid-cols-3 sm:grid-cols-6 gap-3">
            {CHANNELS.map(c => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="bg-white border border-cream-200 rounded-2xl p-3 sm:p-4 text-center hover:shadow-sm transition">
                  <Icon size={20} className="mx-auto text-ink-700"/>
                  <div className="text-xs font-bold text-ink mt-2">{c.label}</div>
                  <div className="text-[10px] text-ink-500 mt-0.5">{c.hint}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section id="numbers" className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-sun text-center">By the numbers</div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-6 sm:gap-8">
            {STATS.map(s => (
              <div key={s.label} className="text-center" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                <div className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">{s.value}</div>
                <div className="text-xs text-white/60 mt-2 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERTICALS */}
      <section id="verticals" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand">Who we serve</div>
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-ink mt-3">
            One stack, five verticals.
          </h2>
          <p className="text-base text-ink-600 mt-3">
            The same backend, the same identity, the same analytics — re-skinned and re-configured for the realities of each place where people eat.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {VERTICALS.map(v => {
            const Icon = v.icon;
            return (
              <Link key={v.to} to={v.to} data-testid={`vertical-${v.to.slice(1)}`}
                className={`group block bg-white rounded-2xl p-6 border ${v.accent} hover:-translate-y-1 hover:shadow-lg transition-all`}>
                <Icon size={28}/>
                <h3 className="font-heading text-xl font-extrabold text-ink mt-4">{v.title}</h3>
                <p className="text-sm text-ink-600 mt-2 leading-relaxed">{v.blurb}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {v.chips.map(c => (
                    <span key={c} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white/80 text-ink-700 border border-current/20">{c}</span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-bold opacity-80 group-hover:opacity-100">
                  Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1"/>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="bg-white border-y border-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand">Platform capabilities</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-ink mt-3">
              Everything from order capture to vendor payout.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map(c => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="p-6 rounded-2xl border border-cream-200 hover:border-ink/30 transition-colors group">
                  <div className="w-11 h-11 rounded-xl bg-cream-100 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                    <Icon size={20}/>
                  </div>
                  <h3 className="font-heading text-lg font-extrabold text-ink mt-4">{c.title}</h3>
                  <p className="text-sm text-ink-600 mt-2 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE DIAGRAM */}
      <section id="architecture" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand">How it fits together</div>
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-ink mt-3">
            Three planes. One brain.
          </h2>
          <p className="text-base text-ink-600 mt-3">
            Customer-facing channels and operations-facing tools all converge on a single, cloud-native backend — so a meal ordered on the app and a meal punched at the POS land in the same ledger.
          </p>
        </div>

        <div className="mt-12 space-y-4">
          {[
            { title: "Customer plane",    nodes: ARCH_NODES_CUSTOMER, color: "bg-amber-50 border-amber-200 text-amber-900" },
            { title: "Operations plane",  nodes: ARCH_NODES_OPS,      color: "bg-violet-50 border-violet-200 text-violet-900" },
            { title: "Admin & analytics", nodes: ARCH_NODES_ADMIN,    color: "bg-emerald-50 border-emerald-200 text-emerald-900" },
          ].map(plane => (
            <div key={plane.title} className={`rounded-2xl border ${plane.color} p-5 grid md:grid-cols-[200px,1fr] gap-5 items-center`}>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em]">{plane.title}</div>
              <div className="grid grid-cols-3 gap-3">
                {plane.nodes.map(n => {
                  const Icon = n.icon;
                  return (
                    <div key={n.label} className="bg-white rounded-xl p-3 border border-current/10 text-center">
                      <Icon size={18} className="mx-auto"/>
                      <div className="text-xs font-bold text-ink mt-2">{n.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {/* Backend bus */}
          <div className="rounded-2xl bg-ink text-white p-6 text-center" data-testid="backend-bus">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-sun">Unified backend</div>
            <div className="mt-2 font-heading text-2xl font-extrabold">Identity · Orders · Wallets · Settlements · Analytics</div>
            <div className="text-xs text-white/60 mt-2">One Mongo + Spring Boot core. Horizontally scaled. AI workers on the side.</div>
          </div>
        </div>
      </section>

      {/* TECH PILLARS */}
      <section id="stack" className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-sun">Engineering principles</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight mt-3">
              Why we own the stack.
            </h2>
            <p className="text-base text-white/70 mt-3">
              Off-the-shelf systems make sense until your customer asks for &ldquo;exactly that, but our way&rdquo; — and you discover you can&apos;t ship it. We built every layer of Grubpe so that answer is always &ldquo;yes, by Friday&rdquo;.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TECH_PILLARS.map(p => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition">
                  <Icon size={22} className="text-sun"/>
                  <h3 className="font-heading text-lg font-extrabold text-white mt-4">{p.title}</h3>
                  <p className="text-sm text-white/70 mt-2 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY GRUBPE - bullets */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand">Why teams switch to Grubpe</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-ink mt-3">
              The bits competitors politely call &ldquo;out of scope&rdquo;.
            </h2>
            <p className="text-base text-ink-600 mt-4 leading-relaxed">
              Multi-vendor cart splits. Real-time vendor payouts. Subsidy rule engines. Compliance-ready GST invoicing per vendor per order.
              The things every operator wants on day one — and discovers their existing POS can&apos;t do on day two.
            </p>
            <button onClick={openLead}
              className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ink text-white font-semibold hover:bg-ink-700">
              See it in action <ArrowRight size={16}/>
            </button>
          </div>
          <div className="space-y-3">
            {[
              "Cart with items from 3 different vendors → 3 KOTs, 3 GST invoices, one customer payment, automatic 3-way settlement.",
              "Corporate subsidy: ₹100 employer-funded daily, employee pays the gap. Auto-reset at month-end. Per-vendor cost split for HR billing.",
              "Auto-recharge wallets for parents that hit ₹100 floor. SMS + email + push, all branded, all tenant-specific.",
              "Offline POS that holds 8 hours of orders, then reconciles to cloud with zero duplicate-order risk when internet returns.",
              "Real-time anomaly alerts: 'vendor X drop-off 38% vs. last Friday' — straight to the regional manager's WhatsApp.",
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-cream-200 p-4">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5"/>
                <p className="text-sm text-ink-700 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <TrendingUp size={36} className="mx-auto text-white/80"/>
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight mt-4">
            Let&apos;s get your operation on Grubpe.
          </h2>
          <p className="text-white/80 mt-3 max-w-xl mx-auto">
            Tell us about your campus, mall, university, or school. We&apos;ll show you a demo built around the way your kitchens, vendors, and customers actually work.
          </p>
          <button onClick={openLead} data-testid="final-cta"
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-brand font-semibold hover:bg-cream-100 active:scale-95">
            Request a demo <ArrowRight size={16}/>
          </button>
        </div>
      </section>

      <MarketingFooter/>
      <LeadModal open={leadOpen} onClose={() => setLeadOpen(false)}/>
    </div>
  );
}
